<script setup lang="ts">
import { ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  UploadIcon,
  LinkIcon,
  TableIcon,
  AiIcon,
  CheckIcon,
} from "../icons";
import { mapRow, validateRecord, formatValue, type Field } from "@yawhub/core";
import FieldEditor from "../components/FieldEditor.vue";
import WebsiteIconField from "../components/WebsiteIconField.vue";
import { api, errorText, useSession } from "../api";
const route = useRoute(),
  router = useRouter(),
  session = useSession();
const mode = ref(String(route.query.mode ?? "manual")),
  name = ref(""),
  description = ref(""),
  website = ref(""),
  fetchFavicon = ref(false),
  busy = ref(false),
  error = ref(""),
  preview = ref<any>(null),
  file = ref<File>(),
  url = ref(""),
  itemsPath = ref(""),
  bearer = ref(""),
  authType = ref("bearer"),
  authUsername = ref(""),
  incrementalPath = ref(""),
  incrementalParameter = ref("updated_since"),
  sheet = ref(""),
  pasted = ref(""),
  method = ref("GET"),
  postBody = ref("{}"),
  readOnlyPost = ref(false),
  pagination = ref("none"),
  nextPath = ref(""),
  pageParam = ref("page");
const fields = ref<Field[]>([
  {
    id: crypto.randomUUID(),
    key: "name",
    label: "Navn",
    type: "text",
    nullable: false,
    queryable: true,
    hidden: false,
    active: true,
  },
  {
    id: crypto.randomUUID(),
    key: "value",
    label: "Værdi",
    type: "decimal",
    nullable: true,
    queryable: true,
    hidden: false,
    active: true,
  },
  {
    id: crypto.randomUUID(),
    key: "date",
    label: "Dato",
    type: "date",
    nullable: true,
    queryable: true,
    hidden: false,
    active: true,
  },
]);
const mapping = ref<any>(null),
  previewRows = computed(() => {
    if (!preview.value || !mapping.value) return [];
    return preview.value.sample.map((r: any) => {
      try {
        return validateRecord(
          { fields: fields.value },
          mapRow(r, mapping.value),
        );
      } catch (e) {
        return { data: {}, issues: [{ message: errorText(e) }] };
      }
    });
  });
function resetMode(value: string) {
  mode.value = value;
  preview.value = null;
  error.value = "";
}
async function getPreview() {
  busy.value = true;
  error.value = "";
  try {
    let body: unknown;
    if (mode.value === "file") {
      const input =
        file.value ??
        (pasted.value
          ? new File([pasted.value], "indsatte-data.csv", { type: "text/csv" })
          : undefined);
      if (!input) throw new Error("Vælg en fil eller indsæt CSV/TSV-data.");
      const form = new FormData();
      if (sheet.value) form.append("sheet", sheet.value);
      form.append("file", input);
      body = form;
    } else
      body = {
        kind: "http",
        config: {
          url: url.value,
          itemsPath: itemsPath.value,
          method: method.value,
          readOnlyPost: readOnlyPost.value,
          ...(method.value === "POST"
            ? { body: JSON.parse(postBody.value) }
            : {}),
          pagination: {
            mode: pagination.value,
            parameter: pageParam.value,
            start: 1,
            ...(pagination.value === "cursor"
              ? { nextPath: nextPath.value }
              : {}),
          },
          ...(incrementalPath.value
            ? {
                incremental: {
                  path: incrementalPath.value,
                  parameter: incrementalParameter.value,
                  overlapSeconds: 300,
                },
              }
            : {}),
        },
        ...(bearer.value
          ? {
              credentials: {
                type: authType.value,
                value: bearer.value,
                ...(authType.value === "basic"
                  ? { username: authUsername.value }
                  : {}),
              },
            }
          : {}),
      };
    preview.value = await api("/api/imports/preview", { method: "POST", body });
    sheet.value = preview.value.selectedSheet ?? "";
    fields.value = preview.value.schema.fields;
    mapping.value = preview.value.mapping;
    if (!name.value)
      name.value = file.value?.name.replace(/\.[^.]+$/, "") ?? "Mit datasæt";
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}
async function improve() {
  busy.value = true;
  error.value = "";
  try {
    const r = await api("/api/imports/" + preview.value.draftId + "/ai", {
      method: "POST",
    });
    fields.value = r.schema.fields;
    mapping.value = r.mapping;
    if (r.name) name.value = r.name;
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}
async function create() {
  busy.value = true;
  error.value = "";
  try {
    let dataset;
    if (
      preview.value?.sheets?.length &&
      sheet.value !== preview.value.selectedSheet
    )
      throw new Error(
        "Opdatér preview for det valgte Excel-ark før aktivering.",
      );
    if (mode.value === "manual")
      dataset = await api("/api/datasets", {
        method: "POST",
        body: {
          name: name.value,
          description: description.value,
          websiteUrl: website.value || null,
          fetchFavicon: fetchFavicon.value,
          schema: { fields: fields.value },
        },
      });
    else {
      const r = await api(
        "/api/imports/" + preview.value.draftId + "/activate",
        {
          method: "POST",
          body: {
            name: name.value,
            description: description.value,
            websiteUrl: website.value || null,
            fetchFavicon: fetchFavicon.value,
            schema: { fields: fields.value },
            mapping: mapping.value,
          },
        },
      );
      dataset = r.dataset;
    }
    await router.push("/datasets/" + dataset.id);
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}
function mappingFor(id: string) {
  return mapping.value.fields.find((m: any) => m.fieldId === id);
}
</script>
<template>
  <RouterLink to="/" class="back-link"
    ><ArrowLeftIcon :size="16" />Alle datasæt</RouterLink
  >
  <div class="page-heading">
    <div>
      <h1>Start med dine data</h1>
      <p class="subtitle">Vælg en kilde. Se et preview. Gør det til dit.</p>
    </div>
  </div>
  <div class="new-dataset-layout">
    <section class="working-panel">
      <div class="segmented source-choice">
        <button
          :class="{ selected: mode === 'manual' }"
          @click="resetMode('manual')"
        >
          <TableIcon :size="18" />Tom tabel</button
        ><button
          :class="{ selected: mode === 'http' }"
          @click="resetMode('http')"
        >
          <LinkIcon :size="18" />API</button
        ><button
          :class="{ selected: mode === 'file' }"
          @click="resetMode('file')"
        >
          <UploadIcon :size="18" />CSV / Excel
        </button>
      </div>
      <form
        v-if="mode !== 'manual' && !preview"
        class="form-stack source-setup"
        @submit.prevent="getPreview"
      >
        <template v-if="mode === 'file'"
          ><label class="upload-area"
            ><UploadIcon :size="30" /><strong>{{
              file?.name || "Vælg en fil at undersøge"
            }}</strong
            ><span>CSV eller XLSX · op til 20 MiB</span
            ><input
              type="file"
              accept=".csv,.xlsx"
              :required="!pasted"
              @change="
                file = ($event.target as HTMLInputElement).files?.[0];
                sheet = '';
              "
          /></label>
          <details>
            <summary>Indsæt data fra udklipsholderen</summary>
            <label
              >CSV eller tabulatorseparerede data<textarea
                v-model="pasted"
                rows="6"
                placeholder="Medtag en række med kolonnenavne."
              />
            </label>
          </details> </template
        ><template v-else
          ><label
            >Kildens URL<input
              v-model="url"
              type="url"
              placeholder="https://api.example.com/activities"
              required /></label
          ><label
            >Sti til listen i JSON<input
              v-model="itemsPath"
              placeholder="Fx data.items — tom for en liste ved roden" /></label
          ><label
            >Adgangsnøgle <span class="muted">(valgfrit)</span
            ><input
              v-model="bearer"
              type="password"
              autocomplete="off"
              placeholder="Gemmes krypteret"
          /></label>
          <details>
            <summary>Pagination og forespørgselsmetode</summary>
            <div class="form-stack">
              <label
                >Godkendelse<select v-model="authType">
                  <option value="bearer">Bearer-token</option>
                  <option value="api-key">API key (X-API-Key)</option>
                  <option value="basic">Brugernavn og adgangskode</option>
                </select></label
              >
              <label v-if="authType === 'basic'"
                >Brugernavn<input v-model="authUsername" autocomplete="off"
              /></label>
              <label
                >Metode<select v-model="method">
                  <option>GET</option>
                  <option>POST</option>
                </select></label
              ><template v-if="method === 'POST'"
                ><label>JSON-body<textarea v-model="postBody" rows="4" /></label
                ><label class="checkbox"
                  ><input
                    type="checkbox"
                    v-model="readOnlyPost"
                    required
                  />Denne POST-forespørgsel læser kun data.</label
                ></template
              ><label
                >Pagination<select v-model="pagination">
                  <option value="none">Ingen</option>
                  <option value="page">Sidenummer</option>
                  <option value="cursor">Cursor</option>
                </select></label
              ><label v-if="pagination !== 'none'"
                >URL-parameter<input v-model="pageParam" /></label
              ><label v-if="pagination === 'cursor'"
                >Sti til næste cursor<input
                  v-model="nextPath"
                  placeholder="pagination.next"
              /></label>
              <label
                >Ændringstid til incremental sync
                <span class="muted">(valgfri JSON-sti)</span
                ><input v-model="incrementalPath" placeholder="updated_at"
              /></label>
              <label v-if="incrementalPath"
                >URL-parameter til ændringer siden<input
                  v-model="incrementalParameter"
              /></label>
            </div></details></template
        ><button class="button primary" :disabled="busy">
          {{ busy ? "Undersøger kilden…" : "Vis preview"
          }}<ArrowRightIcon :size="17" />
        </button>
      </form>
      <form v-else class="form-stack" @submit.prevent="create">
        <div v-if="preview?.sheets?.length > 1" class="form-grid">
          <label
            >Excel-ark<select v-model="sheet">
              <option v-for="s in preview.sheets" :key="s" :value="s">
                {{ s }}
              </option>
            </select></label
          ><button
            type="button"
            class="button secondary"
            :disabled="busy"
            @click="getPreview"
          >
            Opdatér preview for arket
          </button>
        </div>
        <div v-if="preview" class="preview-confirm">
          <span class="status-pill success"
            ><CheckIcon :size="15" />{{ preview.sampleCount }} rækker
            undersøgt</span
          ><button type="button" class="text-button" @click="preview = null">
            Vælg kilde igen</button
          ><button
            v-if="session.capabilities.ai"
            class="button secondary"
            type="button"
            :disabled="busy"
            @click="improve"
          >
            <AiIcon :size="16" />Få AI-forslag
          </button>
        </div>
        <details v-if="preview && session.capabilities.ai" class="small">
          <summary>Hvilke oplysninger sendes til AI?</summary>
          <p>Kun felter med anonyme navne, typer og statistik. Rå eksempelrækker og oprindelige feltnavne sendes ikke. Workspacets AI-politik skal tillade kaldet.</p>
          <ul><li v-for="(field,index) in preview.profile" :key="field.path">field_{{Number(index)+1}}: {{field.type}} · {{field.nulls}} tomme · {{field.distinct}} forskellige værdier</li></ul>
          <RouterLink to="/connections">Se AI-indstillinger</RouterLink>
        </details>
        <div class="form-grid">
          <label
            >Datasættets navn<input
              v-model="name"
              required
              maxlength="120"
              placeholder="Fx Aktiviteter eller Salg" /></label
          ><label
            >Beskrivelse <span class="muted">(valgfri)</span
            ><input
              v-model="description"
              maxlength="1000"
              placeholder="Hvad samler du her?"
          /></label>
        </div>
        <WebsiteIconField
          v-model:website="website"
          v-model:enabled="fetchFavicon"
          :disabled="busy"
        />
        <div>
          <h2>Felter og datatyper</h2>
          <p class="muted">
            Vælg præcise navne. Felter, der kan forespørges, kan bruges i filtre
            og grafer.
          </p>
        </div>
        <FieldEditor v-model="fields" />
        <template v-if="preview"
          ><div class="section-divider" />
          <div>
            <h2>Mapping og identitet</h2>
            <p class="muted">
              Et stabilt ID opdaterer samme række ved næste import.
            </p>
          </div>
          <div class="form-grid">
            <label
              >Identitetsregel<select v-model="mapping.identity.mode">
                <option value="external">Eksternt ID</option>
                <option value="fingerprint">
                  Fingerprint af valgte felter
                </option>
              </select></label
            ><label
              >Identitetsfelter, adskilt med komma<input
                :value="mapping.identity.paths.join(', ')"
                @input="
                  mapping.identity.paths = (
                    $event.target as HTMLInputElement
                  ).value
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean)
                "
                required /></label
            ><label
              >Kildens ændringstid <span class="muted">(valgfri sti)</span
              ><input
                v-model="mapping.updatedAtPath"
                placeholder="Fx updated_at"
                @blur="
                  !mapping.updatedAtPath && delete mapping.updatedAtPath
                " /></label
            ><label
              >Ignorerede stier, adskilt med komma<input
                :value="mapping.ignoredPaths.join(', ')"
                @input="
                  mapping.ignoredPaths = (
                    $event.target as HTMLInputElement
                  ).value
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean)
                "
            /></label>
          </div>
          <p v-if="mapping.identity.mode === 'fingerprint'" class="notice">
            Ændres et identitetsfelt, oprettes en ny række. Brug et eksternt ID,
            hvis kilden har et.
          </p>
          <p v-if="!mapping.updatedAtPath" class="muted small">
            Kilden har ingen valgt versionstid. Senere modtagne data kan
            overskrive tidligere værdier.
          </p>
          <div class="mapping-list">
            <div v-for="f in fields" :key="f.id" class="mapping-row">
              <strong>{{ f.label }}</strong
              ><ArrowLeftIcon :size="16" /><input
                v-if="mappingFor(f.id)"
                v-model="mappingFor(f.id).path"
                :aria-label="'Kildesti til ' + f.label"
              /><select
                v-if="mappingFor(f.id)"
                v-model="mappingFor(f.id).transform"
                :aria-label="'Konvertering til ' + f.label"
              >
                <option value="none">Ingen konvertering</option>
                <option value="string">Til tekst</option>
                <option value="number">Til tal</option>
                <option value="boolean">Til ja/nej</option>
                <option value="date">Til dato</option>
                <option value="datetime">Til tidspunkt</option>
                <option value="json">Til JSON</option></select
              ><select
                v-if="mappingFor(f.id)?.transform === 'date'"
                v-model="mappingFor(f.id).dateFormat"
                aria-label="Datoformat"
              >
                <option value="iso">YYYY-MM-DD</option>
                <option value="dd/MM/yyyy">DD/MM/YYYY</option>
                <option value="MM/dd/yyyy">MM/DD/YYYY</option>
              </select>
            </div>
          </div>
          <h3>Sådan ser de første rækker ud</h3>
          <div
            class="table-scroll"
            role="region"
            aria-label="Preview af mapping"
            tabindex="0"
          >
            <table>
              <thead>
                <tr>
                  <th v-for="f in fields" :key="f.id">{{ f.label }}</th>
                  <th>Validering</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in previewRows" :key="i">
                  <td v-for="f in fields" :key="f.id">
                    {{ formatValue(r.data[f.id], f) }}
                  </td>
                  <td>
                    <span v-if="!r.issues.length" class="status-pill success"
                      >Klar</span
                    ><span v-else class="field-error">{{
                      r.issues.map((x: any) => x.message).join(" ")
                    }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div></template
        >
        <div class="form-actions">
          <RouterLink class="button secondary" to="/">Annullér</RouterLink
          ><button
            class="button primary"
            :disabled="
              busy ||
              !fields.length ||
              (preview?.sheets?.length && sheet !== preview.selectedSheet) ||
              previewRows.some((r: any) => r.issues.length)
            "
          >
            {{
              busy
                ? "Opretter…"
                : preview
                  ? "Godkend og opret datasæt"
                  : "Opret datasæt"
            }}<ArrowRightIcon :size="17" />
          </button>
        </div>
      </form>
      <p v-if="error" class="error" role="alert">{{ error }}</p>
    </section>
    <aside class="context-note">
      <span class="context-line" />
      <h3>Du har det sidste ord</h3>
      <p>
        Et forslag ændrer ikke dine data. Felter og mapping aktiveres først, når
        du godkender dem.
      </p>
      <p>
        Manuel indtastning, gemte mappings og dashboards fungerer også uden AI.
      </p>
    </aside>
  </div>
</template>
